package org.moxultimate.topwithmox.product.ui

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.cards.ContentCard
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.PageHeading
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.statics.ProductStatics
import org.moxultimate.topwithmox.theme.*

/**
 * ProductPage
 * Entry composable for the /products route.
 * Observes [ProductViewModel] state and delegates rendering to [ProductListPage].
 */
@Composable
fun ProductPage() {
    val viewModel: ProductViewModel = koinViewModel()
    val screenState by viewModel.screenState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val hasMore by viewModel.hasMore.collectAsState()
    val allTypes by viewModel.allTypes.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val searchText by viewModel.searchText.collectAsState()

    var lastSuccess by remember { mutableStateOf<ProductState.Success?>(null) }
    if (screenState is ProductState.Success) {
        lastSuccess = screenState as ProductState.Success
    }

    val isFilterReloading = screenState is ProductState.Loading && lastSuccess != null

    Box(
        Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(WhiteBG)
            .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = screenState) {
            is ProductState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    ProductListPage(
                        contents = cached.contentList,
                        allTypes = allTypes,
                        selectedTypes = selectedTypes,
                        allTags = allTags,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMore = hasMore,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                    )
                } else {
                    LoadingIndicator()
                }
            }

            is ProductState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.error,
                onTryAgain = { viewModel.loadProductContents() },
            )

            is ProductState.Success -> ProductListPage(
                contents = state.contentList,
                allTypes = allTypes,
                selectedTypes = selectedTypes,
                allTags = allTags,
                searchText = searchText,
                isLoadingMore = isLoadingMore,
                hasMore = hasMore,
                onTypeToggle = { viewModel.toggleType(it) },
                onSearchChange = { viewModel.setSearchText(it) },
                onClearFilters = { viewModel.clearAllFilters() },
                onLoadMore = { viewModel.loadMoreIfNeeded() },
            )
        }

        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

/**
 * ProductListPage
 * Full scrollable layout: page heading, search bar, type chips, then a responsive grid of
 * [ProductContentCard] items. Attaches an infinite-scroll listener that triggers [onLoadMore].
 *
 * @param contents Accumulated (and optionally filtered) list of product content sections.
 * @param allTypes Full type list for the scrollable chip row.
 * @param selectedTypes Currently active type filter chips.
 * @param searchText Live text filter applied to content title and description.
 * @param isLoadingMore True while the next pagination page is being fetched.
 * @param hasMore False when all pages have been loaded.
 * @param onTypeToggle Toggles a type chip on/off.
 * @param onSearchChange Updates [searchText] and re-filters the content list.
 * @param onClearFilters Resets all chips and search text.
 * @param onLoadMore Triggered by the scroll handler to fetch the next page.
 */
@OptIn(DelicateApi::class)
@Composable
private fun ProductListPage(
    contents: List<ProductContentResponse>,
    allTypes: List<String>,
    selectedTypes: Set<String>,
    allTags: List<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    onTypeToggle: (String) -> Unit,
    onSearchChange: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
) {
    DisposableEffect(hasMore) {
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMore && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    val hasActiveFilters = selectedTypes.isNotEmpty() || searchText.isNotBlank()
    val navigator = rememberAppNavigator()

    Column(
        modifier = if (isMobile)
            Modifier.fillMaxWidth().padding(leftRight = 16.px)
        else
            Modifier
                .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
                .padding(leftRight = 3.cssRem),
    ) {
        PageHeading(
            subtitle = ProductStatics.PAGE_SUBTITLE,
            titleLine1 = ProductStatics.PAGE_TITLE_LINE1,
            titleLine2 = ProductStatics.PAGE_TITLE_LINE2,
            description = ProductStatics.PAGE_DESCRIPTION,
            detail = ProductStatics.PAGE_DETAIL_1,
            detailType1 = ProductStatics.PAGE_DETAIL_TYPE1,
            detail2 = ProductStatics.PAGE_DETAIL_2,
            detailType2 = ProductStatics.PAGE_DETAIL_TYPE2,
            detail3 = ProductStatics.PAGE_DETAIL_3,
            detailType3 = ProductStatics.PAGE_DETAIL_TYPE3,
        )

        Column(
            modifier = Modifier.fillMaxWidth().backgroundColor(Colors.White),
        ) {
            // Search bar row — search input + optional Clear Filters button at its end
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                Box(modifier = Modifier.flexGrow(1)) {
                    SearchBarWithAutocomplete(
                        value = searchText,
                        onValueChange = onSearchChange,
                        suggestions = allTags,
                        placeholder = ProductStatics.SEARCH_PLACEHOLDER,
                        onSearch = onSearchChange,
                    )
                }

                if (hasActiveFilters) {
                    Row(
                        modifier = PressableButtonStyle.toModifier()
                            .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                            .borderRadius(16.px)
                            .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                            .styleModifier { property("background", "#FFFFFF") }
                            .onClick { onClearFilters() },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = "Clear Filters",
                            modifier = Modifier
                                .fontFamily(FontFamily)
                                .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                                .moxTextGradient(),
                        )
                    }
                }
            }

            // Type chips — scrollable row populated from /product-content-type
            if (allTypes.isNotEmpty()) {
                Row(
                    modifier = ChipRowScrollStyle.toModifier()
                        .fillMaxWidth()
                        .padding(top = 12.px, bottom = 4.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.px),
                ) {
                    allTypes.forEach { type ->
                        Chip(
                            description = type,
                            isSelected = type in selectedTypes,
                            onChipClick = { onTypeToggle(type) },
                        )
                    }
                }
            }
        }

        // Content cards grid — wrapping flex row, 3 per row on desktop, full width on mobile
        // align-items: stretch keeps all cards in the same row at equal height
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 24.px)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("align-items", "stretch")
                },
            horizontalArrangement = Arrangement.spacedBy(24.px),
        ) {
            contents.forEach { content ->
                ProductContentCard(
                    content = content,
                    modifier = if (isMobile)
                        Modifier.fillMaxWidth().margin(bottom = 24.px)
                    else
                        Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 24.px),
                    onWatchVideo = {
                        val link = content.videoLink
                        if (!link.isNullOrBlank()) window.open(link, "_blank")
                    },
                    onSeeProducts = { navigator.goToProductDetailPage(content.id) },
                )
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) {
                LoadingMoreIndicator()
            }
        }
    }
}

/**
 * ProductContentCard
 * Delegates to [ContentCard] using product content data.
 * Cover image is taken from the first product in the content list.
 * Renders "Watch Video" (when videoLink present) and "See Products" action buttons.
 *
 * @param content Product content data to display.
 * @param modifier Outer modifier — controls width and margin for grid placement.
 * @param onWatchVideo Callback for the "Watch Video" button.
 * @param onSeeProducts Callback for the "See Products" button.
 */
@Composable
private fun ProductContentCard(
    content: ProductContentResponse,
    modifier: Modifier = Modifier,
    onWatchVideo: () -> Unit,
    onSeeProducts: () -> Unit,
) {
    ContentCard(
        imageSrc = content.thumbnailPath.orEmpty(),
        type = content.type,
        title = content.title,
        description = content.description,
        modifier = modifier,
        actions = {
            if (!content.videoLink.isNullOrBlank()) {
                GradientButton(
                    text = ProductStatics.CARD_WATCH_VIDEO,
                    buttonHeight = 42,
                    onClick = onWatchVideo,
                )
            }
            if (content.products.isNotEmpty()) {
                GradientButton(
                    text = ProductStatics.CARD_SEE_PRODUCTS,
                    buttonHeight = 42,
                    onClick = onSeeProducts,
                )
            }
        },
    )
}
